/**
 * Centralized error handling hook
 * Provides consistent error display and out-of-credits modal handling
 */

import { useState } from 'react';
import { OUT_OF_CREDITS_ERROR_MESSAGE } from '../constants/error-messages';

interface ErrorHandlerReturn {
  error: string | null;
  showOutOfCreditsModal: boolean;
  handleError: (error: Error | string, context?: string) => void;
  clearError: () => void;
  closeOutOfCreditsModal: () => void;
}

export const useErrorHandler = (): ErrorHandlerReturn => {
  const [error, setError] = useState<string | null>(null);
  const [showOutOfCreditsModal, setShowOutOfCreditsModal] = useState(false);

  const handleError = (error: Error | string, context?: string) => {
    const errorMessage = error instanceof Error ? error.message : error;

    // Log to console for debugging
    console.error(`[${context || 'Error'}]:`, errorMessage);

    // Check for out of credits error
    if (errorMessage === OUT_OF_CREDITS_ERROR_MESSAGE) {
      setShowOutOfCreditsModal(true);
      setError(null); // Modal will handle display
    } else {
      setError(errorMessage);
      setShowOutOfCreditsModal(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const closeOutOfCreditsModal = () => {
    setShowOutOfCreditsModal(false);
  };

  return {
    error,
    showOutOfCreditsModal,
    handleError,
    clearError,
    closeOutOfCreditsModal
  };
};
